import { useState, useEffect } from "react";
import { X, Plus } from "lucide-react";
import { useCreateQuinzena } from "@/hooks/use-equipe";
import { useToast } from "@/hooks/use-toast";
import { format, addDays, parseISO } from "date-fns";
import type { Quinzena } from "@shared/schema";

interface NovaQuinzenaSheetProps {
  open: boolean;
  quinzenaAnterior?: Quinzena | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function NovaQuinzenaSheet({ open, quinzenaAnterior, onClose, onSuccess }: NovaQuinzenaSheetProps) {
  const criar = useCreateQuinzena();
  const { toast } = useToast();
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      const base = quinzenaAnterior
        ? format(addDays(parseISO(quinzenaAnterior.periodoFim), 1), "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd");
      const fimDate = format(addDays(parseISO(base), 14), "yyyy-MM-dd");
      setInicio(base);
      setFim(fimDate);
      setError("");
    }
  }, [open, quinzenaAnterior]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const handleCriar = async () => {
    if (!inicio || !fim) return;
    if (fim <= inicio) { setError("Data fim deve ser após a data início"); return; }
    try {
      await criar.mutateAsync({ periodoInicio: inicio, periodoFim: fim });
      toast({ title: "Quinzena criada!" });
      onSuccess();
      onClose();
    } catch {
      toast({ title: "Erro ao criar quinzena", variant: "destructive" });
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
      />
      <div
        className={`fixed bottom-0 left-0 right-0 bg-card rounded-t-2xl z-50 transition-transform duration-300 ease-out shadow-xl ${open ? "translate-y-0" : "translate-y-full"}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-9 h-1 rounded-full bg-border" />
        </div>
        <div className="px-4 pb-8">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold">Nova quinzena</h2>
            <button
              onClick={onClose}
              className="h-8 w-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Data início</label>
              <input
                type="date"
                value={inicio}
                onChange={e => setInicio(e.target.value)}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Data fim</label>
              <input
                type="date"
                value={fim}
                onChange={e => setFim(e.target.value)}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            {error && <p className="text-sm text-erro">{error}</p>}
            <p className="text-xs text-muted-foreground">
              Uma nova quinzena ABERTA será criada. A anterior é mantida para consulta.
            </p>
          </div>

          <button
            onClick={handleCriar}
            disabled={criar.isPending}
            className="mt-6 w-full flex items-center justify-center gap-2 bg-primary hover:opacity-90 disabled:opacity-50 text-primary-foreground rounded-xl py-3 font-medium transition-opacity"
          >
            <Plus className="h-4 w-4" />
            {criar.isPending ? "Criando..." : "Criar quinzena"}
          </button>
        </div>
      </div>
    </>
  );
}
